// Vitals & I/O overview: latest reading per assigned patient. Click through to
// the patient detail to chart new values.
import React, { useEffect, useMemo, useState } from "react";
import { Box, Grid, Paper, Stack, Typography, Avatar, Chip, Button, LinearProgress } from "@mui/material";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";

import { useNurseData, dateFilterParams } from "../NurseDataContext";
import { useMyCoverage } from "../hooks";
import { NURSE_COLORS, initials } from "../theme";
import { getVitalsLatest, listOf } from "../api/nurseApi";
import { vitalChipColor } from "../vitalsThresholds";
import { istDayKey, formatIstTime } from "../../../utils/istDateTime";

export default function VitalsOverview() {
  const { patients, navigate, dateFrom, dateTo } = useNurseData();
  const { myBedCodes } = useMyCoverage();
  const [latest, setLatest] = useState({});
  const [loading, setLoading] = useState(false);

  const dateParams = useMemo(() => dateFilterParams(dateFrom, dateTo), [dateFrom, dateTo]);

  // Only the patients I cover this shift (roster ward ∪ assigned beds).
  const myPatients = useMemo(
    () => (myBedCodes ? patients.filter((p) => p.bed && myBedCodes.has(p.bed)) : []),
    [patients, myBedCodes]
  );

  useEffect(() => {
    if (myPatients.length === 0) return undefined;
    let cancelled = false;
    setLoading(true);
    // Single request: latest vital per admission, keyed by admission id.
    getVitalsLatest(myPatients.map((p) => p.admissionId), dateParams)
      .then((r) => {
        if (cancelled) return;
        const map = {};
        listOf(r).forEach((v) => { map[String(v.admission_id)] = v; });
        setLatest(map);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [myPatients, dateParams]);

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6">Vitals & I/O</Typography>
        <Typography variant="body2" color="text.secondary">Latest readings across your patients. Click a patient to chart new values.</Typography>
      </Box>
      {loading && <LinearProgress sx={{ mb: 2 }} />}
      <Grid container spacing={2}>
        {myPatients.map((p) => {
          const v = latest[p.id];
          return (
            <Grid item xs={12} md={6} lg={4} key={p.id}>
              <Paper sx={{ p: 2, borderRadius: 2, border: `1px solid ${NURSE_COLORS.border}` }}>
                <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", mb: 1 }}>
                  <Avatar sx={{ bgcolor: NURSE_COLORS.primary }}>{initials(p.name)}</Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" noWrap>{p.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{[p.wardName || p.ward, p.bed].filter(Boolean).join(" · ")}</Typography>
                  </Box>
                  <MonitorHeartIcon sx={{ color: NURSE_COLORS.accent }} />
                </Stack>
                {v ? (
                  <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap", mb: 1 }}>
                    <Chip size="small" color={vitalChipColor("bp", v.blood_pressure)} label={`BP ${v.blood_pressure ?? "—"}`} />
                    <Chip size="small" color={vitalChipColor("hr", v.heart_rate)} label={`HR ${v.heart_rate ?? "—"}`} />
                    <Chip size="small" color={vitalChipColor("temp", v.temperature)} label={`Temp ${v.temperature ?? "—"}°`} />
                    <Chip size="small" color={vitalChipColor("spo2", v.oxygen_saturation)} label={`SpO₂ ${v.oxygen_saturation ?? "—"}%`} />
                    <Chip size="small" color={vitalChipColor("rr", v.respiratory_rate)} label={`RR ${v.respiratory_rate ?? "—"}`} />
                  </Stack>
                ) : (
                  <Typography variant="caption" color="text.secondary">No vitals recorded yet.</Typography>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                  Last: {v?.recorded_at ? `${istDayKey(v.recorded_at)} ${formatIstTime(v.recorded_at)}` : "—"}
                </Typography>
                <Button size="small" variant="outlined" fullWidth onClick={() => navigate("patient", { id: p.id })}>
                  Chart vitals
                </Button>
              </Paper>
            </Grid>
          );
        })}
        {myBedCodes !== null && myPatients.length === 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
              <Typography variant="body2" color="text.secondary">No patients in your wards today.</Typography>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
